import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "./AppContext";
import axiosHttp from "../config/axios.interceptor";
import { AuthContext } from "./AuthContext";
import { errorResponseMessage } from "../shared/const.prop";

export const UserProfileContext = createContext();
const reducer = (state, action) => {
  switch (action.type) {
    case "updateUserInfo":
      return {
        ...state,
        [action.updateType]: ["school", "college"].includes(action.updateType)
          ? [...action.payload]
          : { ...action.payload },
        currentPage:
          action.updateType === "profession"
            ? state.currentPage
            : +state.currentPage + 1,
        canActivate: true,
      };
    case "updateCurrentPage":
      return {
        ...state,
        currentPage:
          action?.incOrDec === "dec"
            ? +state.currentPage - 1
            : +state.currentPage + 1,
        canActivate: true,
      };
    default:
      return state;
  }
};

const UserProfileProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const createUserState = {
    currentPage: getCurrentPagePath(),
    userProfile: {
      firstName: "",
      lastName: "",
      dob: "",
      gender: "",
      contactNo: "",
      whatsappNo: "",
      country: "101",
      state: "",
      city: "",
      phoneCode: "",
      language: [],
      linkedinProfile: "",
      facebookProfile: "",
      instagramProfile: "",
      youtubeProfile: "",
      xProfile: "",
      experiences: "",
      timeZone: "",
    },
    school: [{ school: "", className: "", startYear: "", endYear: "" }],
    college: [{ college: "", className: "", startYear: "", endYear: "" }],
    profession: {
      companyDetails: [{ companyName: "", position: "", workExperience: "" }],
      interest: "",
      skills: "",
      experties: "",
      skillsCategory: [],
      offerService: false,
    },

    updateUserInfo: submitHandler,
    routeBack: () => dispatch({ type: "updateCurrentPage", incOrDec: "dec" }),
    pageTitle: [
      "Profile Setup",
      "Education Details",
      "Education Details",
      "Experience Details",
    ],
    canActivate: false,
  };

  const [state, dispatch] = useReducer(useCallback(reducer), createUserState);
  const restructureEducationDetails = (data, propType) => {
    return data.map((educationInfo) => ({
      name: educationInfo.school ?? educationInfo.college,
      className: educationInfo.className,
      startYear: educationInfo.startYear,
      endYear: educationInfo.endYear,
      type: propType === "school" ? "S" : "C",
    }));
  };
  const constructPayload = (data) => {
    const localData = JSON.parse(localStorage.getItem("registerConfirm"));
    if (!localData) return null;
    const code = localData.verification_code;
    const userId = localData.user_id;
    const skills = data.profession?.skills.map((item) => {
      return {
        value: item,
      };
    });
    const experties =
      data?.profession?.experties !== ""
        ? data.profession?.experties?.map((item) => {
            return {
              value: item,
            };
          })
        : "";
    const interest = data.profession?.interest.map((item) => {
      return {
        value: item,
      };
    });

    return {
      userId: localStorage.getItem("userId"),
      education: [
        ...restructureEducationDetails(data.school, "school"),
        ...restructureEducationDetails(data.college, "college"),
      ],
      address: {
        country: data.userProfile.country,
        state: data.userProfile.state,
        city: data.userProfile.city,
      },
      timezone: data.userProfile.timeZone,
      dob: data.userProfile.dob,
      firstName: data.userProfile.firstName,
      lastName: data.userProfile.lastName,
      gender: data.userProfile.gender,
      languages: data.userProfile.language.map((lang) => lang.id),
      linkedinProfile: data.userProfile.linkedinProfile,
      facebookProfile: data.userProfile.facebookProfile,
      instagramProfile: data.userProfile.instagramProfile,
      youtubeProfile: data.userProfile.youtubeProfile,
      xProfile: data.userProfile.xProfile,
      phoneNumber: data.userProfile.contactNo,
      whatsappNumber: data.userProfile.whatsappNo,
      experience: data.profession.companyDetails.map((info) => ({
        ...info,
        experience: info.workExperience,
      })),
      experiences: data.userProfile.experiences,
      offerService: data.profession.offerService,
      skillsCategory: data.profession.skillsCategory,
      skills,
      experties,
      interest,
      code,
      userId,
    };
  };
  function submitHandler(type, userData, updateType) {
    dispatch({
      type,
      payload: userData,
      updateType,
    });
  }

  useEffect(() => {
    if (!state.canActivate) {
      return;
    }
    const currentPage = "/add-profile/step" + state.currentPage;
    navigate(currentPage, { replace: true, relative: "path" });
  }, [state.currentPage]);

  useEffect(() => {
    if (!state.canActivate) return;
    const submitPayload = constructPayload(JSON.parse(JSON.stringify(state)));
    if (!submitPayload) return;
    (async function () {
      appContext.updateLoaderState(true);
      try {
        const submittedData = await axiosHttp.post(
          `profile-setup`,
          submitPayload
        );
        appContext.updateLoaderState(false);
        if (submittedData.status === 201) {
          localStorage.removeItem("userId");
          localStorage.removeItem("registerConfirm");
          appContext.updateNotificationState({
            show: true,
            type: "success",
            message: "You Profile setup has been done successfully",
          });
          setTimeout(() => {
            navigate("../../login", { replace: true, relative: "path" });
          }, 4000);
        }
      } catch (e) {
        //console.log(e);
        if (e.response.status === 429) {
          appContext.updateNotificationState({
            show: true,
            message: errorResponseMessage(e),
            type: "error",
          });
          authContext.logout();
        }
        if (e.response.status === 400) {
          appContext.updateNotificationState({
            show: true,
            message: errorResponseMessage(e),
            type: "error",
          });
        }
        appContext.updateLoaderState(false);
        if (e.response?.status >= 500) {
          appContext.updateNotificationState({
            show: true,
            message: "Something went wrong, please try again later.",
            type: "error",
          });
          return;
        }
        appContext.updateNotificationState({
          show: true,
          type: "error",
          message: errorResponseMessage(e),
        });
      }
    })();
  }, [state.profession]);

  function getCurrentPagePath() {
    if (location.pathname.replace(/\//g, "").endsWith("add-profile")) return 1;
    const lastChar = location.pathname.charAt(location.pathname.length - 1);
    const getCurrentPage = !isNaN(lastChar)
      ? lastChar
      : location.pathname.charAt(location.pathname.length - 2);
    return getCurrentPage;
  }
  return (
    <UserProfileContext.Provider value={state}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileProvider;
